/**
 * @fileoverview Region-aware async variant of the analytics storage gate.
 * @description Lives in its own module (not `consent.ts`) because
 * `hydrate-region-cookie` imports from `consent.ts` — importing it back from
 * there would cycle (`pnpm check:circular`).
 *
 * @module lib/analytics/consent-storage-async
 */

import {
  hasExplicitAnalyticsOptIn,
  isAnalyticsAllowed,
  isAnalyticsStorageAllowed,
} from "./consent"
import { hydrateRegionCookieIfMissing } from "./hydrate-region-cookie"
import { REGION_CONSENT_REQUIRED_COOKIE } from "./region"

/**
 * Region-aware {@link isAnalyticsStorageAllowed}: when the region cookie is
 * ABSENT (cacheable hop, blocked Set-Cookie — the gap #479's hydration layer
 * closes), the sync gate's "no cookie ⇒ rest-of-world" assumption can wrongly
 * permit first-party writes for an EEA/UK/CH visitor. This variant hydrates
 * the region signal first and blocks only when hydration CONFIRMS a
 * consent-required region without an explicit opt-in — the same posture as
 * `recordAttributionTouchAsync` (#479). Unresolved geo / fetch failure
 * proceeds under the rest-of-world default, also matching the attribution
 * layer.
 *
 * Use this for write paths that can await (exposure beacon, ledger rows);
 * the sync gate remains correct whenever the region cookie is present.
 *
 * @returns {Promise<boolean>} True when first-party analytics writes are permitted.
 */
export async function isAnalyticsStorageAllowedAsync(): Promise<boolean> {
  if (!isAnalyticsAllowed()) return false
  if (typeof document === "undefined") return true
  try {
    const header = document.cookie
    const hasRegionCookie =
      header.includes(`${REGION_CONSENT_REQUIRED_COOKIE}=1`) ||
      header.includes(`${REGION_CONSENT_REQUIRED_COOKIE}=0`)
    if (!hasRegionCookie) {
      const outcome = await hydrateRegionCookieIfMissing()
      if (outcome.consentRequired && !hasExplicitAnalyticsOptIn()) {
        return false
      }
    }
  } catch {
    // Region hydration / cookie read failed: don't deny first-party storage
    // for everyone. Fall through to the sync gate (which itself fails open for
    // an unreadable region cookie); the confirmed-EEA case is handled above.
  }
  return isAnalyticsStorageAllowed()
}
